import { Injectable } from '@angular/core';

import { forkJoin, Observable, of, merge } from 'rxjs';
import { map, switchMap, shareReplay, catchError, tap, take } from 'rxjs/operators';
import * as uuid from 'uuid';
import { MessageService } from 'primeng/api';

import { BriefingsApi } from '../../../api/briefings/briefings.api';
import { BriefingTypeEnum } from '@Monitor/Briefings/enums/briefing-type.enum';
import { BriefingListItemModel } from '../model/briefing-list-item.model';
import { BriefingModel } from '../../../Monitor/Briefings/models/briefing.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { BriefingBlockTypeEnum, isMentionBlockTypes } from '../../../Monitor/Briefings/enums/briefing-block-type.enum';
import { BlockModel, LayoutBlockTypesEnum } from '../../PageBuilder/models/block.model';
import { DisplayElementWithSearchCriteriaModel } from '../../../Monitor/Briefings/models/display-element-with-search-criteria.model';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { SavedSearchType } from '../enum/saved-search-type.enum';
import { FilterListProviderServiceInterface } from '../../Filter/services/filter-list-provider-service.interface';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { LayoutBlock } from '../../PageBuilder/models/layout-block.model';
import { BriefingChartDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-chart-display-element.model';
import { ChartDisplayElementModel } from '../../../Common/Reports/models/chart-display-element.model';
import { BriefingApiModel } from '../../../api/briefings/model/briefing-api.model';
import { CuratorBlock } from '../../PageBuilder/models/curator-block.model';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { BriefingListItemApiModel } from '../../../api/briefings/model/briefing-list-item-api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';
import { CuratedMentionListDisplayElementModel } from '../../../Monitor/Briefings/models/curated-mention-list-display-element.model';
import { sortByDate } from '../../Misc/helpers/sort/sort.helper';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';

@Injectable()
export class BriefingTemplateService extends AllResourceAbstractService<BriefingModel, BriefingApiModel, BriefingListItemModel, BriefingListItemApiModel>
    implements FilterListProviderServiceInterface {

    all$: Observable<BriefingListItemModel[]> = this.userStateService.hasAnyPermissions(
        [userPermissionsEnum.advancedMonitoring]
    ).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.fetchList$(),
                this.refreshAllManualSubject
            ) : of([])
        ),
        shareReplay(1)
    );

    constructor(
        private briefingApiService: BriefingsApi,
        private localizationService: LocalizationService,
        private savedSearchesApi: SavedMentionSearchesApi,
        private userStateService: CurrentUserStateService,
        private messageService: MessageService,
    ) {
        super();
    }

    protected fetchList$(): Observable<BriefingListItemModel[]> {
        return this.briefingApiService.getList(BriefingTypeEnum.BRIEFING_TEMPLATE).pipe(
            map(list => list
                .map(item => this.constructListModel(item))
                .sort(sortByDate)
            ),
            catchError((error) => {
                if (error.status === 404) {
                    return of([]);
                }
                throw error;
            })
        );
    }

    getApiService(): MultiBaseAbstractApi<BriefingApiModel, BriefingListItemApiModel> {
        return this.briefingApiService;
    }

    constructResourceModel(apiModel: BriefingApiModel): BriefingModel {
        return new BriefingModel(apiModel);
    }


    constructListModel(apiModel: BriefingListItemApiModel): BriefingListItemModel {
        return new BriefingListItemModel(apiModel);
    }

    getFilterListItems(): Observable<AuditableModel[]> {
        return this.all$;
    }

    refreshAllManual(): Observable<BriefingListItemModel[]> {
        return this.fetchList$().pipe(
            tap((list) => this.refreshAllManualSubject.next(list)),
        );
    }


    createFromBriefing(briefing: BriefingModel): Observable<number> {
        return this.prepareBriefingTemplate(briefing).pipe(
            switchMap((template) => this.create(template))
        );
    }


    updateFromBriefing(briefing: BriefingModel): Observable<number> {
        return this.prepareBriefingTemplate(briefing).pipe(
            switchMap((template) => this.update(template))
        );
    }


    create(template: BriefingModel): Observable<number> {
        return this.isNameDuplicated$(template.name).pipe(
            switchMap(isDuplicate => {
                if (isDuplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.template.duplicateName.error.heading',
                        detail: this.localizationService.transform('Briefings.template.duplicateName.error.message', {
                            name: template.name
                        })
                    });

                    throw new Error(CommonApiErrorEnum.duplicateName);
                }

                return super.createFull(template).pipe(
                    switchMap((x) => this.refreshAllManual().pipe(map(() => x.briefingId)))
                );
            })
        );
    }

    update(template: BriefingModel): Observable<number> {
        return this.all$.pipe(
            take(1),
            switchMap(all => {
                const existing = all.find(x => x.id === template.id);
                const nameChanged = existing?.name !== template.name;

                if (!nameChanged) {
                    return super.updateFull(template);
                }

                return this.isNameDuplicated$(template.name, template.id).pipe(
                    switchMap(isDuplicate => {
                        if (isDuplicate) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Briefings.template.duplicateName.error.heading',
                                detail: this.localizationService.transform('Briefings.template.duplicateName.error.message', {
                                    name: template.name
                                })
                            });

                            throw new Error(CommonApiErrorEnum.duplicateName);
                        }

                        return super.updateFull(template);
                    })
                );
            }),
            switchMap((x) => this.refreshAllManual().pipe(map(() => x.briefingId)))
        );
    }


    delete(template: BriefingListItemModel, broadcast: boolean): Observable<number> {
        const action: ResourceAction = { actionType: ResourceActionTypes.DELETE, resource: template };

        return super.deleteResource(template.id, action, broadcast).pipe(
            switchMap((id) => this.refreshAllManual().pipe(map(() => id)))
        );
    }

    /** Fetch briefing template with complete mention search data */
    getBriefingTemplate(id: number): Observable<BriefingModel> {
        return super.getById(id).pipe(
            switchMap(briefingModel => {

                const template = this.convertToTemplate(briefingModel);

                const blocks = [];
                template.blocks.forEach(block => {
                    if (block.isLayoutBlockType()) {
                        (block as LayoutBlock).blocks.forEach(column => {
                            column.forEach(x => {
                                if (isMentionBlockTypes(x.type) ||
                                    x.type.key === BriefingBlockTypeEnum.ANALYSIS ||
                                    x.type.key === BriefingBlockTypeEnum.CHART) {
                                    blocks.push(x);
                                }
                            });
                        });
                    } else {
                        // if showSectionHeading is true in general styles AND
                        // & isBoundToGlobalStyles is true
                        // & mention block is not in layout block
                        // set showSectionHeading to true in the mention block
                        if (template.generalStyles.mention.showSectionHeading &&
                            block.displayElement.isBoundToGlobalStyles &&
                            isMentionBlockTypes(block.type)) {
                            block.displayElement.styles.showSectionHeading = true;
                        }
                        if (isMentionBlockTypes(block.type) ||
                            block.type.key === BriefingBlockTypeEnum.ANALYSIS ||
                            block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR ||
                            block.type.key === BriefingBlockTypeEnum.CHART) {
                            blocks.push(block);
                        }
                    }
                });

                const displayElements: DisplayElementWithSearchCriteriaModel[] = blocks.map((block) => {
                    return block.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
                });

                const requests: Observable<void>[] = [];

                for (const el of displayElements) {
                    if (el.searchCriteriaId) {
                        const request = this.savedSearchesApi.getById(el.searchCriteriaId).pipe(
                            map((search) => {
                                const mentionSearchModel = new MentionSearchModel(search);
                                mentionSearchModel.id = null;
                                mentionSearchModel.name = null;
                                el.setSearchCriteria(mentionSearchModel);
                            }));
                        requests.push(request);

                        el.searchCriteriaId = null;
                    } else if ((el as unknown as BriefingChartDisplayElementModel).searches) {
                        const chartDisplayElement = el as unknown as BriefingChartDisplayElementModel;
                        const chartSeries = (chartDisplayElement.chartBlock?.displayElement as ChartDisplayElementModel)?.series;

                        chartDisplayElement.searches.forEach((x, seriesIndex) => {
                            delete x.searchReferenceId;
                            if (x.filterSearchCriteriaId) {
                                const request = this.savedSearchesApi.getById(x.filterSearchCriteriaId).pipe(
                                    map(search => {
                                        const mentionSearchModel = new MentionSearchModel(search);
                                        delete mentionSearchModel.id;
                                        delete mentionSearchModel.name;

                                        chartDisplayElement.mentionSearches[seriesIndex] = mentionSearchModel;

                                        if (chartSeries?.[seriesIndex]?.mentions) {
                                            const series = chartSeries[seriesIndex];
                                            series.mentions.filters = mentionSearchModel;
                                            mentionSearchModel.searchCriteria.neoFacets =
                                                (series.termType ? series.getTopTermsChartNeoFacets() : series.mentions.neoFacets) || [];
                                            mentionSearchModel.searchCriteria.facets = series.mentions.facetTypes || [];
                                        }
                                    })
                                );
                                requests.push(request);
                            }
                            delete x.filterSearchCriteriaId;
                        });

                        chartSeries?.forEach(x => {
                            x.seriesUniqueUuid = uuid.v4();
                        });
                    } else {
                        const search = new MentionSearchModel();
                        search.searchCriteria.query.keywordGroups = [];
                        el.setSearchCriteria(search);
                    }
                }

                if (requests.length === 0) {
                    return of(template);
                }

                return forkJoin(requests).pipe(
                    map(x => template)
                );
            })
        );
    }


    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.template.create.error.heading',
                        detail: this.localizationService.transform('Briefings.template.create.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.template.create.heading',
                        detail: this.localizationService.transform('Briefings.template.create.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.template.update.error.heading',
                        detail: this.localizationService.transform('Briefings.template.update.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.template.update.heading',
                        detail: this.localizationService.transform('Briefings.template.update.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Briefings.template.delete.error.heading',
                        detail: this.localizationService.transform('Briefings.template.delete.error.message', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Briefings.template.delete.heading',
                        detail: this.localizationService.transform('Briefings.template.delete.message', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
        }
    }


    /**
     * Prepares a Briefing for persistence as a Briefing Template by performing
     * the following transformations on a cloned copy of the model:
     *
     * - Clones all blocks to decouple the template from the source briefing's state.
     * - Strips non-hosted `chartUrl` values from chart blocks.
     * - Delegates saved search creation and `searchCriteriaId` rebinding for mention/analysis
     *  blocks to {@link setupTopicRequests}, so the template is not tied to the filters of the originating briefing.
     * - Creates new saved searches for each entry in `mentionSearches` on chart
     *   blocks, rebinding each search's `id` to the newly created value.
     * - Clears `mentionIds` from curated mention list blocks, as templates must not
     *   carry mention-specific data.
     */
    private prepareBriefingTemplate(briefingModelClone: BriefingModel): Observable<BriefingModel> {

        // To keep saved searches on
        briefingModelClone.blocks = briefingModelClone.blocks.map(block => block.clone());

        // Save the searches on the template to a new search object, so that modifying the briefing doesn't modify the template
        const blocks: BlockModel[] = [];
        briefingModelClone.blocks.forEach(block => {
            if (block.type.key === LayoutBlockTypesEnum.briefingOne) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if ((isMentionBlockTypes(x.type) || (x.type.key === BriefingBlockTypeEnum.ANALYSIS))) {
                            blocks.push(x);
                        }
                        if (x.type.key === BriefingBlockTypeEnum.CHART) {
                            if ((x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl &&
                                !(x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl.includes('agilitypr.media')) {
                                delete (x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl;
                            }
                        }
                    });
                });
            } else if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        if (x.type.key === BriefingBlockTypeEnum.CHART) {
                            if ((x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl &&
                                !(x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl.includes('agilitypr.media')) {
                                delete (x.displayElement as BriefingChartDisplayElementModel).chart.chartUrl;
                            }
                        }
                    });
                });
            } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                blocks.push(block);
                (block as CuratorBlock).blocks.forEach(x => {
                    if (((isMentionBlockTypes(x.type) || (x.type.key === BriefingBlockTypeEnum.ANALYSIS))) && !x.displayElement.automaticallyGenerated) {
                        blocks.push(x);
                    }
                });
            } else  {
                if ((isMentionBlockTypes(block.type) || (block.type.key === BriefingBlockTypeEnum.ANALYSIS))) {
                    blocks.push(block);
                }
                if (block.type.key === BriefingBlockTypeEnum.CHART) {
                    if ((block.displayElement as BriefingChartDisplayElementModel).chart.chartUrl &&
                        !(block.displayElement as BriefingChartDisplayElementModel).chart.chartUrl.includes('agilitypr.media')) {
                        delete (block.displayElement as BriefingChartDisplayElementModel).chart.chartUrl;
                    }
                }
            }
        });


        const topicRequests: Observable<number>[] = [];
        for (const block of blocks) {
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => {
                    column.forEach(x => {
                        this.setupTopicRequests(topicRequests, x, briefingModelClone);
                    });
                });
            } else {
                this.setupTopicRequests(topicRequests, block, briefingModelClone);
            }
        }

        const searches$ = topicRequests.length ? forkJoin(topicRequests) : of([]);


        const chartFilterRequests: Observable<number>[] = [];
        briefingModelClone.blocks.forEach(block => {
            const chartEls: BriefingChartDisplayElementModel[] = [];
            if (block.type.key === BriefingBlockTypeEnum.CHART) {
                chartEls.push(block.displayElement as BriefingChartDisplayElementModel);
            } else if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks?.forEach(col => col.forEach(inner => {
                    if (inner.type.key === BriefingBlockTypeEnum.CHART) {
                        chartEls.push(inner.displayElement as BriefingChartDisplayElementModel);
                    }
                }));
            }
            chartEls.forEach(chartEl => {
                const chartSeries = (chartEl.chartBlock?.displayElement as ChartDisplayElementModel)?.series;
                (chartEl.mentionSearches ?? []).forEach((mentionSearch, index) => {
                    const searchCriteria = (mentionSearch as MentionSearchModel)?.clone?.(true);
                    if (!searchCriteria) {
                        return;
                    }
                    searchCriteria.name = `${+new Date()}${Math.random()}`;
                    searchCriteria.type = SavedSearchType.BRIEFING;

                    const series = chartSeries?.[index];
                    const searchCriteriaApi = searchCriteria.toApiModel();
                    if (series?.mentions) {
                        searchCriteriaApi.facetSearch = {
                            dateFacets: searchCriteria.searchCriteria.dateFacets,
                            facets: series.mentions.facetTypes || [],
                            neoFacets: (series.termType ? series.getTopTermsChartNeoFacets() : series.mentions.neoFacets) || []
                        };
                    }

                    chartFilterRequests.push(
                        this.savedSearchesApi.create(searchCriteriaApi).pipe(
                            map(id => {
                                chartEl.mentionSearches[index].id = id;
                                return id;
                            })
                        )
                    );
                });
            });
        });

        const chartFilterRequests$ = chartFilterRequests.length ? forkJoin(chartFilterRequests) : of([]);

        return searches$.pipe(
            switchMap(() => chartFilterRequests$),
            map(() => {
                const briefing = new BriefingModel(briefingModelClone.toApiModel());
                const template = this.convertToTemplate(briefing);

                return template;
            })
        );
    }

    /**
     * Builds a saved-search creation request for the given block and pushes it
     * onto `topicRequests`.
     *
     * Clones the block's current search criteria, assigns it a unique throwaway
     * name and the BRIEFING saved-search type, and optionally clears its
     * `dateQuery` when date ranges are not permitted on the block. Once the API
     * call resolves, walks all blocks on `briefingModelClone` — including layout
     * column children and curator child blocks — and rebinds any
     * `searchCriteriaId` that matched the original value to the newly created ID,
     * ensuring the template is fully decoupled from the source briefing's filters.
     *
     * No-ops if the block carries no cloneable search criteria.
     */
    private setupTopicRequests(topicRequests: Observable<number>[], block: BlockModel, briefingModelClone: BriefingModel) {
        const el = (block.displayElement as unknown as DisplayElementWithSearchCriteriaModel);
        const searchCriteria = el.getSearchCriteria()?.clone(true);

        if (searchCriteria) {
            searchCriteria.name = `${+new Date()}${Math.random()}`;
            searchCriteria.type = SavedSearchType.BRIEFING;
            if (!el.dateRangeAllowed && searchCriteria.searchCriteria && searchCriteria.searchCriteria.query) {
                searchCriteria.searchCriteria.query.dateQuery = null;
            }
            topicRequests.push(
                // We're saving a new saved search, so that the template won't be tied to the filters on the existin briefing
                // Once the save is complete, we modify the clone so that it will point to the new ids
                this.savedSearchesApi.create(searchCriteria.toApiModel()).pipe(
                    map((id) => {
                        // Find the blocks on the clone that are using the old ids, and make them use the just created id
                        for (const innerBlock of briefingModelClone.blocks) {
                            const displayElement = innerBlock.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
                            if (displayElement.searchCriteriaId === el.searchCriteriaId) {
                                displayElement.searchCriteriaId = id;
                            }

                            if (innerBlock.type.key === LayoutBlockTypesEnum.briefingOne) {
                                (innerBlock as LayoutBlock).blocks?.forEach(column => {
                                    column.forEach(item => {
                                        const layoutInnerDisplayElement = item.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
                                        if (layoutInnerDisplayElement.searchCriteriaId === el.searchCriteriaId) {
                                            layoutInnerDisplayElement.searchCriteriaId = id;
                                        }
                                    });
                                });
                            }

                            if (innerBlock.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                                const curatorBlock = innerBlock as CuratorBlock;
                                if (curatorBlock.displayElement.searchCriteriaId === el.searchCriteriaId) {
                                    curatorBlock.displayElement.searchCriteriaId = id;
                                }

                                (innerBlock as CuratorBlock).blocks?.forEach(item => {
                                    const curatorInnerDisplayElement = item.displayElement as unknown as DisplayElementWithSearchCriteriaModel;
                                    if (curatorInnerDisplayElement.searchCriteriaId === el.searchCriteriaId) {
                                        curatorInnerDisplayElement.searchCriteriaId = id;
                                    }
                                });
                            }
                        }
                        return id;
                    })
                )
            );
        }
    }


    private convertToTemplate(briefing: BriefingModel): BriefingModel {

        // Briefing Templates can't have mentionIds stored
        for (const block of briefing.blocks) {
            if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks?.forEach((col: BlockModel[]) => col.forEach(inner => {
                    if (inner.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                        (inner.displayElement as CuratedMentionListDisplayElementModel).mentionIds = [];
                        (inner.displayElement as CuratedMentionListDisplayElementModel).mentions = [];
                    }
                }));
            } else {
                if (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                    (block.displayElement as CuratedMentionListDisplayElementModel).mentionIds = [];
                    (block.displayElement as CuratedMentionListDisplayElementModel).mentions = [];
                }
            }
        }

        briefing.cachedMentionsWithInsight = [];

        briefing.type = BriefingTypeEnum.BRIEFING_TEMPLATE;

        return briefing;
    }
}
